import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";

import { EffectAdapter } from "common/effectAdapter";
import { assignedSlotNames } from "common/slot";
import { createTrack, TrackEvent } from "common/track";
import { hash } from "common/helpers";
import { ContextAdapter, ContextAdapterValue } from "tds/contextAdapter";

import { App } from "../appLauncher/types";
import { Config } from "../header/types";
import { NavItem } from "../headerNav/types";
import { UserProfile } from "../headerProfile/types";

import assetAvatarPlaceholder from "./assets/astro-placeholder.svg";

import { trapFocus } from "./focus-trap";

export default class extends LightningElement {
  @api apps: App[] | null = null;
  @api help: Config["help"] | null = null;
  @api labels: { [key: string]: string } = {};
  @api logo: Config["logo"] | null = null;
  @api navItems: NavItem[] | null = null;
  @api user: UserProfile | null = null;
  @api signinLink: Config["signinLink"] | null = null;
  @api signupLink: Config["signupLink"] | null = null;
  @api inboxNotification: Config["inboxNotification"];

  @wire(ContextAdapter)
  private context: ContextAdapterValue = null!;

  private slots = "";
  private track = createTrack<"tds-header-mobile">(this);

  private get assets() {
    return {
      avatarPlaceholder: `${this.context.assetsUrl}${assetAvatarPlaceholder}`,
    };
  }

  private get navSections() {
    let navItems = this.navItems || [];
    return [navItems]
      .concat(this.userNavSections)
      .filter((section) => section.length > 0)
      .map((items) => ({
        items,
        key: hash(items.map((i) => i.label).join("")),
      }));
  }

  private get section1ClassName() {
    return classNames("section-1", {
      "section-1--show-menu": this.showMenuButton,
    });
  }

  private get showMenuButton() {
    return this.user || this.navItems;
  }

  private get userNavSections() {
    try {
      return [
        this.user!.navItems.filter((i) => i.mobile === true),
        [this.user!.logout],
      ];
    } catch {
      return [];
    }
  }

  private open = false;
  private removeFocusTrap: ReturnType<typeof trapFocus> | undefined = undefined;

  private handleAppLauncherTrack(e: TrackEvent<"tds-app-launcher">) {
    let eventName = e.detail.eventName;
    let name: `app_launcher_${typeof eventName}` = `app_launcher_${eventName}`;
    this.track(name, e.detail.payload);
  }

  private handleHelpButtonClick() {
    this.track("help_clicked", undefined);
  }

  private handleLogoClick() {
    this.track("logo_clicked", undefined);
  }

  private handleNavTrack(e: TrackEvent<"tds-header-nav">) {
    let eventName = e.detail.eventName;
    let name: `nav_${typeof eventName}` = `nav_${eventName}`;
    this.track(name, e.detail.payload);
  }

  private handleProfileTrack(e: TrackEvent<"tds-header-profile">) {
    let eventName = e.detail.eventName;
    let name: `profile_${typeof eventName}` = `profile_${eventName}`;
    this.track(name, e.detail.payload);
  }

  private handleSigninClick() {
    this.track("signin_clicked", { label: this.labels.logIn });
  }

  private handleSignupClick() {
    this.track("signup_clicked", { label: this.labels.signUp });
  }

  private handleInboxNotificationClick() {
    this.track("community_inbox_clicked", { label: this.labels.messages });
  }

  private onMenuClick() {
    this.open = true;
  }

  private onMenuCloseClick() {
    this.open = false;
  }

  @wire(EffectAdapter, { open: "$open" })
  private onOpen() {
    document.body.style.overflow = this.open ? "hidden" : "";
  }

  private onSlotChange() {
    this.slots = assignedSlotNames(this.template).join(" ");
  }

  renderedCallback() {
    if (this.open && !this.removeFocusTrap) {
      this.removeFocusTrap = trapFocus(this.template.querySelector(".menu")!);
    }
    if (!this.open && this.removeFocusTrap) {
      this.removeFocusTrap();
      this.removeFocusTrap = undefined;
    }
  }
}
